import React, { useState, useMemo } from 'react';
import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useGetRecentMattersQuery } from 'utils/wemble-api';
import { MatterEntry } from 'utils/wemble-api.generated';
import { MatterPM } from 'components/Home/DetailedTable';
import Modal from '../Modal';
import MatterModal from './MatterModal';
import ActivitySparkline from './ActivitySparkline';
import { useCardTableStyles } from './styles';
import { formatTimeAgo } from 'utils/helpers';

const INITIAL_LIMIT = 5;
const PERIOD_STORAGE_KEY = 'wemble_matters_period';
const EXPANDED_STORAGE_KEY = 'wemble_matters_expanded';

const useStyles = makeStyles((theme) => ({
  matter__name: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  table_subtitle: {
    fontSize: '12 !important',
    [theme.breakpoints.down('xs')]: {
      fontSize: '10px !important',
    },
  },
  periodToggle: {
    fontSize: 11,
    fontWeight: 500,
    padding: '3px 8px',
    borderRadius: 6,
    cursor: 'pointer',
    border: 'none',
    transition: 'background-color 0.15s, color 0.15s, transform 0.1s',
    userSelect: 'none' as const,
    '&:active': {
      transform: 'scale(0.93)',
    },
  },
  periodToggleActive: {
    backgroundColor: '#38566B',
    color: 'white',
  },
  periodToggleInactive: {
    backgroundColor: '#f0f0f0',
    color: '#888',
    '&:hover': {
      backgroundColor: '#e4e4e4',
    },
  },
  showAll: {
    fontSize: 12,
    color: theme.palette.gray.g2,
    cursor: 'pointer',
    padding: '6px 0',
    textAlign: 'center' as const,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  leadAvatar: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: 6,
    backgroundColor: '#e8ecf0',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  leadInitial: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 6,
    backgroundColor: '#e8ecf0',
    fontSize: 10,
    fontWeight: 600,
    color: '#666',
    verticalAlign: 'middle',
  },
}));

const LeadAvatar = ({ name, profilePicture }: { name: string; profilePicture?: string }) => {
  const classes = useStyles();
  const src = profilePicture
    ? 'https://d378wr66tg5qg7.cloudfront.net/' + profilePicture
    : null;

  return (
    <Tooltip title={name} arrow placement="top">
      {src ? (
        <span className={classes.leadAvatar}>
          <img alt={name} src={src} />
        </span>
      ) : (
        <span className={classes.leadInitial}>{name.charAt(0)}</span>
      )}
    </Tooltip>
  );
};

const RecentMattersTable = ({ userId, defaultDays, enableHours, enableComments = false }: { userId: string; defaultDays: number; enableHours: boolean; enableComments?: boolean }) => {
  const classes = useStyles();
  const tableClasses = useCardTableStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [days, setDays] = useState(() => {
    const stored = localStorage.getItem(PERIOD_STORAGE_KEY);
    return stored ? parseInt(stored) : defaultDays;
  });
  const [expanded, setExpanded] = useState(() => localStorage.getItem(EXPANDED_STORAGE_KEY) === 'true');

  const updateDays = (d: number) => {
    setDays(d);
    setExpanded(false);
    if (d === defaultDays) {
      localStorage.removeItem(PERIOD_STORAGE_KEY);
    } else {
      localStorage.setItem(PERIOD_STORAGE_KEY, String(d));
    }
  };
  const [selectedMatter, setSelectedMatter] = useState<MatterEntry | null>(null);
  const { data: recentMatters } = useGetRecentMattersQuery({ userId }, { refetchOnMountOrArgChange: true });

  const billableMatters = useMemo(() => {
    if (!recentMatters) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const cutoffStr = cutoff.toISOString().split('T')[0];

    return recentMatters
      .filter(m => !m.nonBillable)
      .map(m => {
        // Recalculate totalHours from dailyActivity for the selected period
        const periodHours = (m.dailyActivity || [])
          .filter(d => d.date >= cutoffStr)
          .reduce((sum, d) => sum + d.hours, 0);
        // Latest work date with hours on this matter (YYYY-MM-DD, so string compare works)
        const lastActivity = (m.dailyActivity || [])
          .filter(d => d.hours > 0)
          .reduce((max, d) => (d.date > max ? d.date : max), '');
        return { ...m, totalHours: parseFloat(periodHours.toFixed(1)), lastActivity };
      })
      .filter(m => m.totalHours > 0)
      // Most recently worked-on matter first; hours in the period break ties
      .sort((a, b) => b.lastActivity.localeCompare(a.lastActivity) || b.totalHours - a.totalHours);
  }, [recentMatters, days]);
  const showComments = enableComments && billableMatters.some(m => m.comment);
  const visibleMatters = expanded ? billableMatters : billableMatters.slice(0, INITIAL_LIMIT);
  const hasMore = billableMatters.length > INITIAL_LIMIT;
  const hasActivity = enableHours && billableMatters.some(m => m.dailyActivity && m.dailyActivity.length > 0);
  // Firms whose data has no responsible partner (e.g. Selmer from TIQ) get no empty Lead column.
  const hasLead = billableMatters.some(m => m.leadName);

  const sparklineMax = useMemo(() => {
    let max = 0;
    for (const m of billableMatters) {
      for (const d of m.dailyActivity || []) {
        if (d.hours > max) max = d.hours;
      }
    }
    return max || 1;
  }, [billableMatters]);

  // Section-level freshness: the most recent time a matter was entered/edited
  // across everything we fetched. Use this (not work date) because it tells the
  // reader how stale the data is, rather than when the person last worked.
  const lastEntryAt = useMemo(() => {
    if (!recentMatters) return undefined;
    let max: string | undefined;
    for (const m of recentMatters) {
      if (m.lastUpdatedAt && (!max || m.lastUpdatedAt > max)) max = m.lastUpdatedAt;
    }
    return max;
  }, [recentMatters]);

  // If no data at all (API returned nothing), hide the section entirely
  if (!recentMatters || recentMatters.filter(m => !m.nonBillable).length === 0) return null;

  return (
    <>
      <div className={tableClasses.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2>Recent Matters</h2>
            {lastEntryAt && (
              <span style={{ fontSize: 11, opacity: 0.4, marginTop: 5 }}>
                Last entry {formatTimeAgo(lastEntryAt)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {[7, 14, 30].map(d => (
              <span
                key={d}
                onClick={() => updateDays(d)}
                className={`${classes.periodToggle} ${days === d ? classes.periodToggleActive : classes.periodToggleInactive}`}
              >
                {d}d
              </span>
            ))}
          </div>
        </div>
        {billableMatters.length === 0 ? (
          <div style={{ padding: '16px 0', textAlign: 'center', color: '#999', fontSize: 13 }}>
            No billable matters in the past {days} days
          </div>
        ) : (<>
        <Table className={tableClasses.table}>
          <TableHead>
            <TableRow>
              <TableCell>Matter</TableCell>
              {showComments && <TableCell>Comment</TableCell>}
              {hasActivity && !isMobile && <TableCell style={{ minWidth: 200 }}>Activity</TableCell>}
              {enableHours && <TableCell align={'right'} style={{ whiteSpace: 'nowrap' }}>{`Hours (${days}d)`}</TableCell>}
              {hasLead && <TableCell align={'right'} style={{ whiteSpace: 'nowrap' }}>Lead</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleMatters.map((matter) =>
              <React.Fragment key={`matter-${matter.matterExternalId}`}>
                <TableRow>
                  <TableCell>
                    <span className={classes.matter__name} onClick={() => setSelectedMatter(matter)}>
                      {matter.matterName}
                    </span>
                    <MatterPM matter={{ number: matter.matterExternalId, manager: matter.manager }} userId={userId} />
                    <br /><span className={classes.table_subtitle} style={{ marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {matter.confidential
                        ? <LockOutlinedIcon style={{ fontSize: 11, opacity: 0.5 }} />
                        : matter.clientCountry ? <span className={`fi fi-${matter.clientCountry.toLowerCase()}`} style={{ fontSize: 11 }} /> : null
                      }
                      <span style={{ opacity: 0.5 }}>{matter.clientName}</span>
                    </span>
                  </TableCell>
                  {showComments && <TableCell className={classes.table_subtitle}>{matter.comment}</TableCell>}
                  {hasActivity && !isMobile && (
                    <TableCell align={'right'} style={{ paddingBlock: 0 }}>
                      <ActivitySparkline
                        dailyHours={matter.dailyActivity || []}
                        maxHours={sparklineMax}
                        showLabels
                        highlightDays={days}
                      />
                    </TableCell>
                  )}
                  {enableHours && <TableCell align={'right'} style={{ whiteSpace: 'nowrap' }}>{matter.totalHours?.toFixed(1)}</TableCell>}
                  {hasLead && (
                    <TableCell align={'right'} style={{ paddingBlock: 0 }}>
                      {matter.leadName ? (
                        <div className={tableClasses.roundPill}>{matter.leadName}</div>
                      ) : null}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow className={tableClasses.spacer} />
              </React.Fragment>
            )}
          </TableBody>
        </Table>
        {hasMore && (
          <div className={classes.showAll} onClick={() => { const next = !expanded; setExpanded(next); next ? localStorage.setItem(EXPANDED_STORAGE_KEY, 'true') : localStorage.removeItem(EXPANDED_STORAGE_KEY); }}>
            {expanded ? 'Show less' : `Show ${billableMatters.length - INITIAL_LIMIT} more`}
          </div>
        )}
        </>)}
      </div>
      {selectedMatter && (
        <Modal visible={!!selectedMatter} close={() => setSelectedMatter(null)} customWidth={1100}>
          <MatterModal
            matterNumber={selectedMatter.matterExternalId}
            matterName={selectedMatter.matterName}
            clientName={selectedMatter.clientName}
            clientCountry={selectedMatter.clientCountry}
            confidential={selectedMatter.confidential}
            leadPartnerName={selectedMatter.leadName}
            leadUserId={selectedMatter.leadUserId}
          />
        </Modal>
      )}
    </>
  );
};

export default RecentMattersTable;
